import {
  ProductCategory,
  getProductCategory
} from '@spokeldn/common-sku-functions'
import {
  ReboundOrder,
  ReturnReason,
  ReturnsProduct,
  ReboundReturnedItem,
  ReboundSalesItem,
  ReboundReason
} from '../types'
import { getMeta, gidNum, mapSizes } from './mappedReboundApiToReturn'
import { decodeProductId } from '@helpers/shopify'
import {
  SalesItemParsed,
  ZSalesItemValidator
} from '../validators/reboundApiSchema'
import { ExchangeOptions } from '@components/returns/types'
import { ZProductBase } from '../validators/returnSchema'
import { formatZodError } from '../helpers/zodErrorFormatter'

const firstNonEmpty = (...vals: Array<string | null | undefined>) =>
  vals.find((v): v is string => typeof v === 'string' && v.trim().length > 0)

function resolveImageUrl(
  raw: ReboundReturnedItem | ReboundSalesItem
): string | undefined {
  const fromPictures = raw.pictures?.[0]?.url

  const metaKeys = ['image']
  const fromMeta = raw?.clientMetadata
    ? metaKeys.map(k => getMeta(raw.clientMetadata, k)).find(Boolean)
    : undefined

  return firstNonEmpty(fromPictures, fromMeta)
}

function asSalesLike(raw: ReboundReturnedItem | ReboundSalesItem): unknown {
  const r = raw

  const imageUrl = resolveImageUrl(r)
  const productType = firstNonEmpty(r.productType)
  const title = firstNonEmpty(r.productType)
  const sku = firstNonEmpty(r.sku)
  const color = firstNonEmpty(r.title)

  if (!productType)
    throw new Error(
      'ZSalesItemValidator: productType is required (returned item)'
    )
  if (!title)
    throw new Error('ZSalesItemValidator: title is required (returned item)')
  if (!sku)
    throw new Error('ZSalesItemValidator: sku is required (returned item)')

  return {
    productType,
    sku,
    title,
    color,
    fullPrice: r.fullPrice,
    commercialValue: r.commercialValue, // sane fallback if not available
    sizes: r.sizes ?? {},
    pictures: imageUrl ? [{ url: imageUrl }] : [],
    clientMetadata: r.clientMetadata ?? [],
    isMTO: r.isMTO,
    isReturnable: r.isReturnable,
    isExchangeable: r.isExchangeable,
    exchangeOptions: r.exchangeOptions,
    salesItemId: r.salesItemId
  }
}

function formatReasons(reason: ReboundReason): ReturnReason {
  return {
    id: reason.returnReasonId,
    title: reason.text,
    fields: reason.fields?.map(field => ({
      label: field.label,
      type: field.type,
      values: field.values?.map(value => ({
        label: value.label,
        value: value.value,
        id: value.id
      }))
    }))
  }
}

type ValidReasonCategory =
  | ProductCategory.TROUSERS
  | ProductCategory.SHORTS
  | ProductCategory.TOPS

function mergeReasonsReplaceFields(
  reasons: ReboundOrder['reasons'],
  category: ValidReasonCategory
): ReturnReason[] {
  const globalList = reasons.data ?? []
  const categoryList = category ? reasons[category] ?? [] : []

  const merged = new Map<number, ReboundReason>()

  for (const r of [...globalList, ...categoryList]) {
    const prev = merged.get(r.returnReasonId)
    if (!prev) {
      merged.set(r.returnReasonId, r)
    } else {
      // Replace only `fields` with the newer (category) fields if present.
      merged.set(r.returnReasonId, {
        ...prev,
        fields: r.fields ?? prev.fields
      })
    }
  }

  return Array.from(merged.values()).map(formatReasons)
}

const normalizeKey = (str: string, isType = false): string =>
  str
    .replace(/\s+/g, '')
    .replace(/ShortsLeg/i, 'Leg')
    [isType ? 'toLowerCase' : 'toString']()

const mapExchangeOptions = (
  sizes?: Array<{ name: string; type: string; values: string[] }>
): ExchangeOptions => {
  if (!sizes) return []
  return sizes.map(({ name, type, values }) => {
    const normalizedName = normalizeKey(name).replace('ChestSize', 'Chest')
    const normalizedType = normalizeKey(type, true).replace(
      'chest_size',
      'size'
    )

    return {
      name: normalizedName,
      type: normalizedType,
      values
    }
  })
}

export function mapReboundSaleItemToReturnProduct(
  rawItem: ReboundReturnedItem | ReboundSalesItem,
  newResp: ReboundOrder,
  idx = 0
) {
  const ctx = `mapNewItemToLegacyProduct:item[${rawItem.salesItemId}]`

  const normalized = asSalesLike(rawItem)

  const item: SalesItemParsed = ZSalesItemValidator.parse(normalized)

  const shopifyProductId = getMeta(item.clientMetadata, 'shopifyProductId')
  const shopifyVariantId = getMeta(item.clientMetadata, 'shopifyVariantId')
  const shopifyLineItemId = getMeta(
    item.clientMetadata,
    'shopifyOrderLineItemId'
  )
  if (!shopifyProductId || !shopifyLineItemId) {
    throw new Error(`${ctx}: Missing required Shopify metadata`)
  }

  const variantId = gidNum(shopifyVariantId)
  const id = `${gidNum(shopifyLineItemId)}${idx > 0 ? `-${idx}` : ''}`.trim()

  if (!id) {
    throw new Error(`${ctx}: Missing required Shopify ids`)
  }

  const amount = item.commercialValue.amount.toFixed(2)
  const image = item?.pictures ? item?.pictures[0]?.url : ''
  const productType = item.productType
  const title = item.title
  const sku = item.sku
  const productCategory = getProductCategory(sku)

  const returnReasons = mergeReasonsReplaceFields(
    newResp.reasons,
    productCategory as ValidReasonCategory
  )

  const product: ReturnsProduct = {
    product_id: Number(decodeProductId(shopifyProductId)),
    id,
    sale_item_id: item.salesItemId,
    variant_id: Number(variantId || '0'),
    product_type: productType,
    image,
    sizes: mapSizes(productType, item.sizes),
    paid_price: Number(amount),
    full_price: item.fullPrice,
    isMTO: item.isMTO,
    title,
    sku,
    color: item.color,
    isReturnable: Boolean(item.isReturnable),
    isExchangeable: Boolean(item.isExchangeable),
    exchange_options: mapExchangeOptions(item.exchangeOptions),
    exchange_colors: [],
    return_reasons: returnReasons
  }

  const validatedProducts = ZProductBase.safeParse(product)

  if (!validatedProducts.success) {
    const formatted = formatZodError(validatedProducts.error, product)
    console.error('Zod validation failed for products object:\n' + formatted)
    throw new Error('Invalid products object')
  }

  return validatedProducts.data
}
